import { useCallback, useMemo } from 'react';
import { FormApi } from 'final-form';

import { useExecuteOperations } from '@occc-app/offerHelpers/operations/useExecuteOperations';
import { useChainId } from '@occc-app/Chain/hooks';
import { useIsUserPermittedTo } from '@occc/utils-access-control';
import { useConfig } from '@occc/config';

import {
  getInAudienceConstraint,
  getSubBrandConstraintInitialValues,
  useGetStoreLocationOrGroupsInitialValues,
} from '@occc/feat-constraints';
import { getInitialValues, getTargetsOperations } from './utils';
import {
  TargetFormPropsHookArgs,
  TargetFormProps,
  TargetFormValues,
  TargetTabProps,
} from './types';

import { GetFormPropsHook } from '../types';
import { mapFormTypeToPermission } from '../utils/offerPermission';
import { useGetAudienceById } from '../../dataAccessLayer/audience/useGetAudienceById';

export const useGetTargetFormProps: GetFormPropsHook<TargetFormPropsHookArgs, TargetFormProps> = ({
  offerId,
  offer,
  type,
  onError,
  offerLoading,
  onCancel,
  onFormSubmitted,
  targetTabProps,
  currentFlowType,
  onRefetchOffer,
  isRefetching,
}) => {
  const chainId = useChainId();
  const { config } = useConfig();

  const isFormEditable = useIsUserPermittedTo(mapFormTypeToPermission[type]);

  const getTargetTabProps = (): TargetTabProps | undefined => {
    if (targetTabProps && typeof targetTabProps === 'function') {
      return targetTabProps(offer);
    }

    return targetTabProps;
  };

  const resolvedTargetTabProps = getTargetTabProps();
  const {
    shouldShowStateRestrictions,
    shouldShowStoreLocationOrGroups,
    shouldShowSubBrands,
    customSection,
  } = resolvedTargetTabProps ?? {};

  const getOperations = useCallback(
    (
      currentOfferId: string,
      currentOffer: typeof offer,
      values: TargetFormValues,
      dirtyFields: Parameters<typeof getTargetsOperations>[3],
    ) => [
      ...getTargetsOperations(currentOfferId, currentOffer, values, dirtyFields),
      ...(customSection?.getOperations?.(currentOfferId, currentOffer, values, dirtyFields) ?? []),
    ],
    [customSection],
  );

  const [executeOperations, loading] = useExecuteOperations<TargetFormValues>({
    offerId,
    offer,
    getOperations,
  });

  const saveTargetConfiguration = async (
    values: TargetFormValues,
    form: FormApi<TargetFormValues>,
  ) => {
    try {
      await executeOperations(values, form.getState());
      await onFormSubmitted();
    } catch (error) {
      onError(error);
    }
  };

  const inAudienceConstraint = useMemo(() => getInAudienceConstraint(offer), [offer]);
  const { data: audience } = useGetAudienceById(inAudienceConstraint?.audienceId, chainId);
  const subBrandsList = config.subBrands?.list || [];

  const storeLocationInitialValues = useGetStoreLocationOrGroupsInitialValues(offer);
  const subBrand = getSubBrandConstraintInitialValues(offer, subBrandsList);

  const initialValues = getInitialValues(offer, storeLocationInitialValues, audience, subBrand);

  return {
    initialValues,
    chainId,
    isFormEditable,
    onSubmit: saveTargetConfiguration,
    loading: offerLoading || loading,
    onCancelClick: onCancel,
    currentFlowType,
    offerId,
    onRefetchOffer,
    isRefetching,
    shouldShowStateRestrictions,
    shouldShowStoreLocationOrGroups,
    shouldShowSubBrands,
    customSection,
  };
};
